let currencySymbol = '$';
if (theme.settings.storeCurrency && Shopify) {
  const { currency: { active } } = Shopify;
  const { storeCurrency } = theme.settings;
  const current = storeCurrency.find(({ ISO }) => ISO === active );
  if (current) currencySymbol = current.symbol;
}

const moneyFormat = (amount) => {
  return `${currencySymbol}${(amount / 100).toFixed(2)}`;
}

const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchCart = async () => {
  try {
    const res = await fetch('/cart.js');
    const data = await res.json();
    if (res.ok) return data;
  } catch (error) {
    console.log(error);
    return null
  }
}

class CartThresholdFST extends HTMLElement {
  constructor() {
    super();

    this.enabledTargets = [];
    this.shippingTargets = [];
    this.gwpTargets = [];
    this.discountTargets = [];
    this.maxTarget = 0;

    this.script = this.querySelector('script');
    this.data = this.script ? JSON.parse(this.script.textContent) : null;
    if (!this.data) return;

    const settings = this.data.settings || {};
    this.shippingEnabled = settings.shipping_enabled ?? settings.enabled ?? false;
    this.gwpEnabled = settings.gwp_enabled ?? false;
    this.discountEnabled = settings.discount_enabled ?? false;
    this.defaultMessage = settings.default ?? '';
    this.highestOnly = settings.highest_only ?? settings.highest ?? false;

    const shippingTargetsRaw = Array.isArray(this.data.shippingTargets)
      ? this.data.shippingTargets
      : Array.isArray(this.data.targets)
        ? this.data.targets
        : [];
    const gwpTargetsRaw = Array.isArray(this.data.gwpTargets) ? this.data.gwpTargets : [];
    const discountTargetsRaw = Array.isArray(this.data.discountTargets) ? this.data.discountTargets : [];

    shippingTargetsRaw.forEach((target) => this.#addTarget(target, 'shipping'));
    gwpTargetsRaw.forEach((target) => this.#addTarget(target, 'gwp'));
    discountTargetsRaw.forEach((target) => this.#addTarget(target, 'discount'));

    if (!this.enabledTargets.length) return;

    this.enabledTargets.sort((a, b) => a.value - b.value);
    this.enabledTargets.forEach((target, index) => {
      target.index = index;
    });

    this.maxTarget = this.enabledTargets[this.enabledTargets.length - 1]?.value ?? 0;
    if (this.maxTarget === 0) return;

    this.#renderElements();
    this.#init();

    document.addEventListener('cart:updated', (evt) => {
      const { detail: { cart } } = evt;
      this.#calculateShipping(cart);
    });
  }

  #addTarget(target, type) {
    if (!target) return;
    if (type === 'shipping' && !this.shippingEnabled) return;
    if (type === 'gwp' && !this.gwpEnabled) return;
    if (type === 'discount' && !this.discountEnabled) return;

    const value = Number(target.value ?? 0);
    if (Number.isNaN(value) || value <= 0) return;

    const normalisedTarget = {
      ...target,
      value,
      type,
      messaging: target.messaging || {}
    };

    this.enabledTargets.push(normalisedTarget);
    if (type === 'shipping') this.shippingTargets.push(normalisedTarget);
    if (type === 'gwp') this.gwpTargets.push(normalisedTarget);
    if (type === 'discount') this.discountTargets.push(normalisedTarget);
  }

  #convertToCurrency(amount) {
    return Shopify?.currency?.rate ? Math.trunc(Shopify.currency.rate * +amount) : Math.trunc(+amount);
  }

  async #init() {
    const cartData = await fetchCart();
    this.#calculateShipping(cartData);
  }

  #renderElements() {
    const initialTarget = this.#convertToCurrency(this.maxTarget);
    const renderProgressDots = this.enabledTargets.map(({ name, label, value, index, type }) => {
      return `
        <span
          class="promo-dots promo--${name} promo--${name}-${index}"
          data-target="${value}"
          data-type="${type}"
          data-currency-symbol="${currencySymbol}"
          data-label="${label}"
          aria-hidden="true"
          style="--position: 0">
        </span>
      `;
    }).join('');

    const html = `
      <div id="cart-promotions-message-success-fst" class="cart-promotions-message-success appear-animation appear-delay-1" style="display: none;"></div>
      <div id="cart-promotions-message-fst" class="cart-promotions-message appear-animation appear-delay-1"></div>
      <div class="cart-promotions-wrapper cart-promotions-wrapper-fst appear-animation appear-delay-1" data-target="${initialTarget}" data-currency-symbol="${currencySymbol}">
        <label for="cart-promotions" class="visually-hidden">Cart Promotions</label>
        <progress id="cart-promotions-fst" max="100" value="0" data-target="${initialTarget}"></progress>
        <span class="promo-dots promo--value"></span>
        ${renderProgressDots}
      </div>
    `;

    this.innerHTML = html;
  }

  #collectTotals(cartJson) {
    if (!cartJson) {
      return {
        base: 0,
        shipping: 0,
        gwp: 0,
        hideShipping: false
      };
    }

    let total = 0;
    if (cartJson.total_price && cartJson.total_price > 0) {
      total = cartJson.total_price;
    } else if (cartJson.final_price && cartJson.final_price > 0) {
      total = cartJson.final_price;
    }

    let dropshipTotalPrice = 0;
    let ignoreGWPTotal = 0;
    let hideShipping = false;

    (cartJson.items || []).forEach((item) => {
      const properties = item.properties || {};
      const propertyKeys = Object.keys(properties);
      if (propertyKeys.includes('_no-free-shipping')) {
        hideShipping = true;
      }
      if (propertyKeys.includes('_dropship')) {
        dropshipTotalPrice += item.final_line_price;
      }
      if (propertyKeys.includes('_ignore-gwp')) {
        ignoreGWPTotal += item.final_line_price;
      }
    });

    const baseTotal = total > 0 ? total / 100 : 0;
    const shippingTotal = total > 0 ? Math.max((total - dropshipTotalPrice) / 100, 0) : 0;
    const gwpTotal = total > 0 ? Math.max((total - ignoreGWPTotal) / 100, 0) : 0;

    return {
      base: baseTotal,
      shipping: shippingTotal,
      gwp: gwpTotal,
      hideShipping
    };
  }

  #getEligibleTotal(totals, type) {
    if (!totals) return 0;
    if (type === 'shipping') return totals.shipping;
    if (type === 'gwp') return totals.gwp;
    if (type === 'discount') return totals.base;
    return totals.base;
  }

  #updatePromoDots(activeTargets, maxTargetValue) {
    const activeSet = new Set(activeTargets);
    const dots = [];
    
    // Calculate initial positions for all active dots
    this.enabledTargets.forEach((target) => {
      const dot = this.querySelector(`.promo--${target.name}-${target.index}`);
      if (!dot) return;

      if (activeSet.has(target)) {
        const convertedTargetValue = this.#convertToCurrency(target.value);
        // Position based on highest target = 100%, so position = (targetValue / maxTargetValue) * 100
        const position = maxTargetValue > 0 ? Math.min((convertedTargetValue / maxTargetValue) * 100, 100) : 0;
        dots.push({ dot, target, position, value: convertedTargetValue });
        dot.removeAttribute('hidden');
        // Restore original label if dot becomes visible but not yet active
        if (!dot.classList.contains('active')) {
          dot.setAttribute('data-label', target.label || '');
        }
      } else {
        dot.setAttribute('hidden', 'hidden');
        dot.classList.remove('active');
        dot.style.setProperty('--position', 0);
        // Restore original label when hidden
        dot.setAttribute('data-label', target.label || '');
      }
    });

    // Adjust positions to ensure minimum spacing
    const adjustedDots = this.#adjustDotSpacing(dots);
    
    // Apply adjusted positions
    adjustedDots.forEach(({ dot, position }) => {
      dot.style.setProperty('--position', position);
    });
  }

  #adjustDotSpacing(dots) {
    if (!dots || dots.length === 0) return dots;
    
    // Minimum spacing between dots as percentage
    // With 35px dots and labels, we need at least 12% spacing to prevent overlap
    const minSpacingPercent = 12;
    
    // Sort dots by their calculated position (ascending)
    const sortedDots = [...dots].sort((a, b) => a.position - b.position);
    const adjustedDots = [];
    
    // If we have multiple close dots, we might need to distribute them evenly
    // Find groups of close dots and redistribute them
    for (let i = 0; i < sortedDots.length; i++) {
      const currentDot = sortedDots[i];
      let adjustedPosition = currentDot.position;
      
      if (i > 0) {
        const prevDot = adjustedDots[i - 1];
        const spacingPercent = adjustedPosition - prevDot.position;
        
        // If dots are closer than minimum spacing, adjust current dot position
        if (spacingPercent < minSpacingPercent) {
          // Move current dot forward to maintain minimum spacing
          adjustedPosition = prevDot.position + minSpacingPercent;
          
          // Don't exceed 100%
          if (adjustedPosition > 100) {
            // If we're at the limit, try to compress previous dots slightly
            // But only if we have enough space
            const availableSpace = 100 - adjustedDots[0].position;
            const requiredSpace = minSpacingPercent * adjustedDots.length;
            
            if (requiredSpace <= availableSpace && i > 0) {
              // Redistribute dots evenly within available space
              const firstPosition = adjustedDots[0].position;
              const evenSpacing = (100 - firstPosition) / (adjustedDots.length + 1);
              
              adjustedDots.forEach((dot, idx) => {
                if (idx === 0) return; // Keep first dot position
                dot.position = firstPosition + (evenSpacing * (idx + 1));
              });
              
              adjustedPosition = firstPosition + (evenSpacing * (adjustedDots.length + 1));
            } else {
              adjustedPosition = 100;
            }
          }
        }
      }
      
      adjustedDots.push({
        ...currentDot,
        position: adjustedPosition
      });
    }
    
    return adjustedDots;
  }

  #calculateShipping(cartJson) {
    const totals = this.#collectTotals(cartJson);
    const container = this.querySelector('#cart-promotions-fst');
    const outerContainer = this.querySelector('.cart-promotions-wrapper-fst');
    const valueDot = this.querySelector('.promo--value');
    const messageWrapper = this.querySelector('#cart-promotions-message-fst');
    const successMessageWrapper = this.querySelector('#cart-promotions-message-success-fst');

    if (!container || !outerContainer || !messageWrapper || !successMessageWrapper) return;

    const activeTargets = this.enabledTargets.filter((target) => !(target.type === 'shipping' && totals.hideShipping));

    if (!activeTargets.length) {
      container.setAttribute('value', 0);
      outerContainer.setAttribute('value', 0);
      outerContainer.classList.add('hide');
      messageWrapper.innerHTML = '';
      messageWrapper.classList.remove('show');
      successMessageWrapper.innerHTML = '';
      successMessageWrapper.classList.remove('show');
      return;
    }

    const highestTarget = activeTargets[activeTargets.length - 1];
    const maxTargetValue = this.#convertToCurrency(highestTarget.value);
    const progressBaseTotal = this.#getEligibleTotal(totals, highestTarget.type);
    // Highest target equals 100%, so progress = (cartTotal / highestTarget) * 100
    const progressValueRaw = maxTargetValue > 0 ? (progressBaseTotal / maxTargetValue) * 100 : 0;
    const progressValue = Math.max(0, Math.min(progressValueRaw, 100));

    container.setAttribute('value', progressValue ? progressValue : 0);
    outerContainer.setAttribute('value', progressValue ? progressValue : 0);
    outerContainer.dataset.target = maxTargetValue;
    container.dataset.target = maxTargetValue;

    if (valueDot) {
      valueDot.style.setProperty('--position', progressValue > 100 ? 100 : progressValue);
    }

    this.#updatePromoDots(activeTargets, maxTargetValue);

    let message = '';
    const successMessages = [];
    let allPromosUnlocked = true;

    for (const target of activeTargets) {
      const promoDot = this.querySelector(`.promo--${target.name}-${target.index}`);
      const eligibleTotal = this.#getEligibleTotal(totals, target.type);
      const targetValueConverted = this.#convertToCurrency(target.value);
      const remaining = targetValueConverted - eligibleTotal;

      if (remaining <= 0) {
        if (promoDot) {
          promoDot.classList.add('active');
          promoDot.setAttribute('data-label', 'Unlocked');
        }
        if (target.messaging?.success) {
          let successMsg = target.messaging.success.replaceAll('[title]', target.title ?? '');
          if (target.type === 'discount' && target.saveAmount) {
            const formattedSave = `${currencySymbol}${target.saveAmount.toFixed(2)}`;
            successMsg = successMsg.replace('[save]', formattedSave);
          }
          successMessages.push(`<div class="cart-promotion-success-item">${successMsg}</div>`);
        }
      } else {
        allPromosUnlocked = false;
        if (promoDot) {
          promoDot.classList.remove('active');
          // Restore original label when not active
          const originalLabel = target.label || '';
          promoDot.setAttribute('data-label', originalLabel);
        }

        if (!message && target.messaging?.ongoing) {
          const formattedRemaining = `${currencySymbol}${remaining ? remaining.toFixed(2) : target.value.toFixed(2)}`;
          let ongoingMsg = target.messaging.ongoing
            .replace('[remaining]', formattedRemaining)
            .replaceAll('[title]', target.title ?? '');
          if (target.type === 'discount' && target.saveAmount) {
            const formattedSave = `${currencySymbol}${target.saveAmount.toFixed(2)}`;
            ongoingMsg = ongoingMsg.replace('[save]', formattedSave);
          }
          message = ongoingMsg;
        }
      }
    }

    // Check if all active targets are unlocked
    if (allPromosUnlocked && activeTargets.length > 0) {
      message = 'All Promos Unlocked';
      outerContainer.classList.remove('hide');
    } else if (progressValue >= 100 && !allPromosUnlocked) {
      outerContainer.classList.add('hide');
    } else {
      outerContainer.classList.remove('hide');
    }

    if (!message && !allPromosUnlocked) {
      const firstTarget = activeTargets[0];
      if (firstTarget?.type === 'shipping' && this.defaultMessage && this.#getEligibleTotal(totals, 'shipping') <= 0) {
        message = this.defaultMessage;
      }
    }

    const successMessageContent = successMessages.join('');

    messageWrapper.innerHTML = message;
    messageWrapper.classList.toggle('show', Boolean(message));

    // Hide success message wrapper temporarily
    successMessageWrapper.innerHTML = successMessageContent;
    successMessageWrapper.style.display = 'none';
    successMessageWrapper.classList.remove('show');
  }
}

customElements.define('widget-cart-threshold-fst', CartThresholdFST);

class CartThresholdPromoTiers extends HTMLElement {
  constructor() {
    super();

    this.enabledTargets = [];
    this.maxTarget = 0;

    this.script = this.querySelector('script');
    this.data = this.script ? JSON.parse(this.script.textContent) : null;
    this.enabled = this.data ? this.data.settings.enabled : null;
    this.highest = this.data ? this.data.settings.highest : null;
    if (!this.data || !this.enabled) return;

    const [ tier1, tier2, tier3 ] = this.data.targets;
    if (tier1.value > 0) {
      this.enabledTargets.push(tier1);
    }
    if (tier2.value > 0) {
      this.enabledTargets.push(tier2);
    }
    if (tier3.value > 0) {
      this.enabledTargets.push(tier3);
    }

    if (!this.enabledTargets.length) return;
    this.enabledTargets = this.enabledTargets.sort((a, b) => a.value - b.value);

    const allTargets = this.enabledTargets.map(({ value }) => value);
    this.maxTarget = Math.max(...allTargets);
    if (this.maxTarget === 0) return;

    this.#renderElements();
    this.#init();

    document.addEventListener('cart:updated', (evt) => {
      const { detail: { cart } } = evt;
      this.#calculateShipping(cart);
    });
  }

  #convertToCurrency(amount) {
    return Shopify?.currency?.rate ? Math.trunc(Shopify.currency.rate * +amount) : Math.trunc(+amount)
  }

  async #init() {
    const cartData = await fetchCart();
    this.#calculateShipping(cartData);
  }

  #renderElements() {
    const target= this.#convertToCurrency(this.maxTarget);
    const renderProgressDots = this.enabledTargets.map(({ name, label, value }, index) => {
      return `
        <span
          class="promo-dots promo--${name} promo--${name}-${index}"
          data-target="${value}"
          data-currency-symbol="${currencySymbol}"
          data-label="${label}"
          aria-hidden="true"
          style="--position: ${(value/this.maxTarget) * 98}">
        </span>
      `
    }).join('');

    const html = `
      <div id="cart-promotions-message-success-tiers" class="cart-promotions-message-success appear-animation appear-delay-1"></div>
      <div id="cart-promotions-message-tiers" class="cart-promotions-message appear-animation appear-delay-1"></div>
      <div class="cart-promotions-wrapper cart-promotions-wrapper-tiers appear-animation appear-delay-1" data-target="${target}" data-currency-symbol="${currencySymbol}">
        <label for="cart-promotions" class="visually-hidden">Cart Promotions - Free Gift</label>
        <progress id="cart-promotions-tiers" max="100" value="0" data-target="${target}"></progress>
        <span class="promo-dots promo--value"></span>
        ${renderProgressDots}
      </div>
    `;

    this.innerHTML = html;
  }

  #calculateShipping(cartJson) {
  
    const targets = this.enabledTargets;
    let total = cartJson.total_price && cartJson.total_price > 0 ? cartJson.total_price : cartJson.final_price && cartJson.final_price > 0 ? cartJson.final_price : 0;
    let ignoreGWPTotal = 0;
    cartJson.items.forEach(item => {
      if (item.properties?.["_ignore-gwp"]) {
        ignoreGWPTotal += item.final_line_price;
      }
    })
    const cartTotal = total > 0 ? (total - ignoreGWPTotal) / 100 : 0;

    const progressValue = (cartTotal / this.#convertToCurrency(this.maxTarget)) * 100;	
    const container = document.getElementById('cart-promotions-tiers');	
    const outerContainer = document.querySelector('.cart-promotions-wrapper-tiers');

    let message = '';
    let messageSuccess = '';

    if (progressValue >= 100) {	
      container.setAttribute('value', 100);	
      container.parentElement.setAttribute('value', 100);	
      outerContainer.classList.add('hide');	
    } else {	
      container.setAttribute('value', progressValue ? progressValue : 0);	
      container.parentElement.setAttribute('value', progressValue ? progressValue : 0);	
      outerContainer.classList.remove('hide');	
    }

    if (this.querySelector('.promo--value')) {	
      this.querySelector('.promo--value').style.setProperty('--position', progressValue > 100 ? 100 : progressValue);	
    }


    if (!this.maxTarget) {
      message = 'Please enter target amounts in Theme Settings > Cart Promotions'
      return;
    }

    if (cartTotal <= 0) {
      message = '';
      
    } else {
      for (const [index, target] of targets.entries()) {
        const promoDot = this.querySelector(`.promo--${target.name}-${index}`);
        const remaining = this.#convertToCurrency(target.value) - cartTotal;

        if (remaining <= 0) {
          messageSuccess = `<span>${target.messaging.success.replaceAll('[title]', target.title ?? '')}</span><br/>` ?? '';
          if (promoDot) promoDot.classList.add('active');
        } else {
          message += target.messaging.ongoing.replace('[remaining]', `${currencySymbol}${remaining ? remaining.toFixed(2) : target.value.toFixed(2)}`).replaceAll('[title]', target.title ?? '') ?? '';
          if (promoDot) promoDot.classList.remove('active');
          break;
        }
      }
    }

    // Display message
    let messageWrapper = document.getElementById('cart-promotions-message-tiers');
    messageWrapper.innerHTML = message;
    message ? messageWrapper.classList.add('show') : messageWrapper.classList.remove('show');

    let successMessageWrapper = document.getElementById('cart-promotions-message-success-tiers');
    successMessageWrapper.innerHTML = messageSuccess;
    messageSuccess ? successMessageWrapper.classList.add('show') : successMessageWrapper.classList.remove('show');

    // document.getElementById('cart-promotions-message-tiers').innerHTML = message;
    // document.getElementById('cart-promotions-message-success-tiers').innerHTML = messageSuccess;
  }
}

customElements.define('widget-cart-threshold-tiers', CartThresholdPromoTiers);